import { ApolloClient, createHttpLink, InMemoryCache, split } from '@apollo/client/core'
import { getMainDefinition } from '@apollo/client/utilities'
import { setContext } from '@apollo/client/link/context'
import { useAuth } from './hooks/auth'
import { WebSocketLink } from './graphql-ws'
import { watch, computed } from 'vue'
import { StorageSerializers, useFetch, useIntervalFn, useLocalStorage } from '@vueuse/core'
import { getAuth } from 'firebase/auth'
import { Kind, OperationTypeNode } from 'graphql'

const localDiscover = useFetch('http://ropescore.local').get().text()
const resolvedReachable = useFetch(
  computed(() => `https://${localDiscover.data.value}/.well-known/apollo/server-health`),
  {
    refetch: computed(() => typeof localDiscover.data.value === 'string' && /\.local\.ropescore\.com(:\d+)?$/.test(localDiscover.data.value)),
    immediate: false,
    timeout: 5_000
  }
).get().json()
useIntervalFn(() => {
  void localDiscover.execute().then(async () => {
    if (typeof localDiscover.data.value === 'string' && /\.local\.ropescore\.com(:\d+)?$/.test(localDiscover.data.value)) {
      await resolvedReachable.execute()
    }
  })
}, 60_000)

export const localServers = [
  { text: 'local-001 (sw rpi)', value: 'local-001' },
  { text: 'local-002 (sw dell)', value: 'local-002' },
  { text: 'local-003 (ak)', value: 'local-003' },
  { text: 'local-004 (sau)', value: 'local-004' },
  { text: 'local-005 (amjrf)', value: 'local-005' }
]
export const regionalServers = [
  { text: 'Belgium', value: 'europe-west1', url: 'ropescore-api-224455118938.europe-west1.run.app' },
  { text: 'South Korea', value: 'asia-northeast3', url: 'ropescore-api-224455118938.asia-northeast3.run.app' }
]
export const manualServer = useLocalStorage<{ type: 'regional' | 'local' | 'dev', selector?: string } | null>('rs-api', null, {
  serializer: StorageSerializers.object
})

const manualReachable = useFetch(
  computed(() =>
    manualServer.value?.type === 'dev'
      ? 'http://localhost:5000/.well-known/apollo/server-health'
      : `https://${manualServer.value?.selector}.local.ropescore.com/.well-known/apollo/server-health`),
  {
    refetch: computed(() => manualServer.value?.type === 'dev' || (manualServer.value?.type === 'local' && manualServer.value.selector != null)),
    timeout: 5_000,
    immediate: manualServer.value?.type === 'dev' || (manualServer.value?.type === 'local' && manualServer.value.selector != null)
  }
).get().json()
useIntervalFn(() => {
  void manualReachable.execute()
}, 60_000)

export const apiDomain = computed(() => {
  if (manualServer.value?.type === 'dev' && manualReachable.data.value?.status === 'pass') return 'localhost:5000'
  if (manualServer.value?.type === 'local' && manualServer.value.selector && manualReachable.data.value?.status === 'pass') return `${manualServer.value.selector}.local.ropescore.com`
  else if (
    typeof localDiscover.data.value === 'string' &&
    /\.local\.ropescore\.com(:\d+)?$/.test(localDiscover.data.value) &&
    resolvedReachable.data.value?.status === 'pass'
  ) {
    return localDiscover.data.value.trim()
  }

  if (manualServer.value?.type === 'regional' && manualServer.value.selector) {
    const config = regionalServers.find(s => s.value === manualServer.value?.selector)
    if (config != null) return config.url
  }
  return 'api.ropescore.com'
})

function createLink () {
  const wsLink = new WebSocketLink({
    url: () => { return import.meta.env.VITE_GRAPHQL_WS_ENDPOINT ?? `${apiDomain.value.includes('localhost') ? 'ws' : 'wss'}://${apiDomain.value}/graphql` },
    lazy: true,
    connectionParams: async () => {
      const oldAuth = useAuth()
      const offOld = watch(oldAuth.token, () => {
        console.log('restarting due to old auth')
        wsLink.client.restart()
        offOld()
      })

      const auth = getAuth()
      let firstChange = false
      const offNew = auth.onIdTokenChanged(() => {
        if (!firstChange) {
          firstChange = true
          return
        }
        console.log('restarting due to new auth')
        wsLink.client.restart()
        offNew()
      })

      const token = oldAuth.token.value
      const firebaseToken = await new Promise<string | undefined>((resolve, reject) => {
        const off = auth.onAuthStateChanged(user => {
          off()
          if (user) {
            user.getIdToken()
              .then(token => { resolve(token) })
              .catch((err: Error) => { reject(err) })
          } else resolve(undefined)
        })
      })

      return {
        Authorization: token ? `Bearer ${token}` : '',
        'Firebase-Authorization': firebaseToken ? `Bearer ${firebaseToken}` : ''
      }
    }
  })

  const httpLink = createHttpLink({
    uri: () => { return import.meta.env.VITE_GRAPHQL_ENDPOINT ?? `${apiDomain.value.includes('localhost') ? 'http' : 'https'}://${apiDomain.value}/graphql` }
  })

  const authLink = setContext(async (_, { headers }) => {
    const oldAuth = useAuth()
    const auth = getAuth()
    const token = oldAuth.token.value
    const firebaseToken = await new Promise<string | undefined>((resolve, reject) => {
      const off = auth.onAuthStateChanged(user => {
        off()
        if (user) {
          user.getIdToken()
            .then(token => { resolve(token) })
            .catch((err: Error) => { reject(err) })
        } else resolve(undefined)
      })
    })

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        'firebase-authorization': firebaseToken ? `Bearer ${firebaseToken}` : ''
      }
    }
  })

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === Kind.OPERATION_DEFINITION &&
        definition.operation === OperationTypeNode.SUBSCRIPTION
      )
    },
    wsLink,
    authLink.concat(httpLink)
  )

  return splitLink
}

const cache = new InMemoryCache({
  typePolicies: {
    User: {
      fields: {
        streamShares: {
          merge: false
        }
      }
    }
  },
  possibleTypes: {
    Scoresheet: ['TallyScoresheet', 'MarkScoresheet']
  }
})

export const apolloClient = new ApolloClient({
  link: createLink(),
  cache
})

export const apolloClientAlternate = new ApolloClient({
  link: createLink(),
  cache
})
